<template>
  <div>
    <Banner type="order" />
    <div class="page-container j-container-box-shadow container">
      <div class="title">
        <div class="left-section">
          <div class="back" @click="handleBack">
            <BackArrowIcon class="icon" />
            <span class="icon-label">{{ $t('order.back') }}</span>
          </div>

          <a-divider type="vertical" style="margin: 0 30px; height: 28px" />

          <div class="order-title">
            {{ $t('order.order_title') }}
          </div>
        </div>

        <div class="action-section" @click="handleDownload">
          <DownloadIcon class="icon" />
          <span class="action-label">{{ $t('order.download') }}</span>
        </div>
      </div>

      <a-divider style="margin: 30px 0" />

      <div class="info">
        <a-row>
          <a-col :xs="24" :sm="24" :md="8">
            <a-descriptions :column="1" :colon="false">
              <a-descriptions-item :label="$t('order.order_no')">
                {{ detail.orderNum }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.order_date')">
                {{ moment(detail.commitTime).format('YYYY-MM-DD') }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.total_price')">
                {{ getTotalPrice() }}
              </a-descriptions-item>
            </a-descriptions>
          </a-col>
          <a-col :xs="24" :sm="24" :md="8">
            <a-descriptions :column="1" :colon="false">
              <a-descriptions-item :label="$t('order.order_consignee')">
                {{ detail.consignee }}
              </a-descriptions-item>
              <a-descriptions-item :label="$t('order.status')">
                {{ getOrderStatus(detail.orderStatus) }}
              </a-descriptions-item>
              <a-descriptions-item
                class="j-custom-description-item"
                :label="$t('order.remark')"
              >
                {{ detail.remark }}
              </a-descriptions-item>
            </a-descriptions>
          </a-col>
        </a-row>
      </div>

      <div class="table-container">
        <div class="table-title">{{ $t('order.table_title') }}</div>

        <a-table
          :pagination="false"
          :columns="columns"
          size="middle"
          row-key="id"
          :data-source="dataSource"
        >
          <template #name="{text, record}">
            <a @click="gotoDiagramDetail(record)">{{ $t(text || '') }}</a>
          </template>

          <template #modelNum="{text, record}">
            <a @click="gotoDiagramDetail(record)">{{ text }}</a>
          </template>

          <template #part-code="{text, record}">
            <a @click="gotoDiagramDetail(record)">{{ text }}</a>
          </template>

          <template #unit="{text}">
            {{ text ? $t(text) : '' }}
          </template>

          <template #atlaName="{text}">
            {{ text ? $t(text) : '' }}
          </template>

          <template #unitPrice="{text}">
            {{ text !== null ? numeral(text).format('$0,0.00') : '' }}
          </template>

          <template #totalPrice="{text}">
            {{ text !== null ? numeral(text).format('$0,0.00') : '' }}
          </template>

          <template #[`${col}-title`] v-for="col in colTitle" :key="col">
            {{ $t(`order.${col}`) }}
          </template>
        </a-table>

        <div class="table-summary">
          <div>{{ $t('order.total_price') }}</div>
          <div class="price">{{ totalSummary }}</div>

          <a-button
            v-if="userInfo?.orderPart === 1"
            :loading="isCopying"
            @click="handleReorder"
            class="submit-btn"
            type="primary"
            >{{ $t('order.reorder') }}</a-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { createVNode, defineComponent } from 'vue';
import { mapState } from 'vuex';
import { Modal } from 'ant-design-vue';
import { ExclamationCircleOutlined } from '@ant-design/icons-vue';
import moment from 'moment';
import numeral from 'numeral';
import Banner from '@/components/banner/Banner.vue';
import BackArrowIcon from '@/components/icons/BackArrowIcon.vue';
import DownloadIcon from '@/components/icons/DownloadIcon.vue';
import { getOrderDetail, getInternaion, copyOrder } from '@/api/apis';
import { downloadFile } from '@/api/request';
import { baseUrl } from '@/utils/constants';

interface PartOrderDetailProps {
  detail: any;
  [key: string]: any;
}

export default defineComponent({
  name: 'PartOrderDetail',
  components: {
    Banner,
    BackArrowIcon,
    DownloadIcon
  },
  created() {
    const id = +(this.$route.params.id as string);
    if (!isNaN(id) && id > 0) {
      this.id = id;
      this.loadData();
    }
  },
  data(): PartOrderDetailProps {
    return {
      moment,
      numeral,
      id: 0,
      loading: false,
      colTitle: [
        'vehicle_name',
        'part_type',
        'part_name',
        'part_no',
        'part_code',
        'unit',
        'quantity',
        'unit_price',
        'total_price',
        'remark'
      ],
      columns: [
        {
          dataIndex: 'vehicleName',
          width: 120,
          slots: { title: 'vehicle_name-title', customRender: 'vehicleName' }
        },
        {
          dataIndex: 'atlaName',
          width: 120,
          slots: { title: 'part_type-title', customRender: 'atlaName' }
        },
        {
          dataIndex: 'name',
          width: 100,
          slots: { customRender: 'name', title: 'part_name-title' }
        },
        {
          dataIndex: 'modelNum',
          width: 100,
          slots: { title: 'part_no-title', customRender: 'modelNum' }
        },
        {
          dataIndex: 'partCode',
          width: 100,
          slots: { title: 'part_code-title', customRender: 'part-code' }
        },
        {
          dataIndex: 'unit',
          width: 100,
          slots: { customRender: 'unit', title: 'unit-title' }
        },
        {
          dataIndex: 'totalQuantity',
          width: 100,
          slots: { customRender: 'quantity', title: 'quantity-title' }
        },
        {
          dataIndex: 'sellingPrice',
          width: 100,
          slots: { customRender: 'unitPrice', title: 'unit_price-title' }
        },
        {
          dataIndex: 'totalPrice',
          width: 100,
          slots: { customRender: 'totalPrice', title: 'total_price-title' }
        },
        {
          dataIndex: 'remark',
          width: 120,
          slots: { title: 'remark-title' }
        }
      ],
      dataSource: [],
      detail: {},
      moneyTypes: [],
      isCopying: false
    };
  },
  methods: {
    getTotalPrice() {
      const res = numeral(0);

      this.detail.offSellingPrice
        ? res.add(this.detail.offSellingPrice || 0)
        : res.add(this.detail.totalSellingPrice || 0);
      const currency = this.getCurrency(this.detail);

      const value = res.value();
      return value ? currency + res.format('0,0.00') : '';
    },
    async handleDownload() {
      const paramArr: string[] = [];

      paramArr.push(`customerId=${this.userInfo.id}`);
      paramArr.push(`locale=${this.lang}`);
      const moneyType = this.detail.moneyType
        ? this.$t(this.detail.moneyType)
        : '';
      if (moneyType) {
        paramArr.push(`moneyType=${moneyType}`);
      }

      await downloadFile(
        `${baseUrl}ureport/excel?_u=db:user_part_order_detail_export.ureport.xml&orderId=${
          this.id
        }&${paramArr.join('&')}`,
        this.$t('order.order_title') + '.xlsx',
        null
      );
    },
    getCurrency(record) {
      let res = '';
      const moneyType = record.moneyType;

      if (moneyType) {
        const item: any = this.moneyTypes.find(
          (s: any) => s.code === moneyType
        );

        res = item ? item.val : '';
      }
      return res;
    },
    getOrderStatus(status) {
      let res = '';

      if (!isNaN(status) && status !== null && status >= 0) {
        res = this.$t(`order_status.${status}`);
      }
      return res;
    },
    async loadData() {
      this.loading = true;

      const dRes = await getInternaion({ type: 8 }); // 货币;

      if (dRes.success) {
        this.moneyTypes = dRes.result || [];
      }

      const res = await getOrderDetail({
        id: this.id
      });

      if (res.success) {
        this.detail = res.result || {};

        this.dataSource = this.detail.orderDetailList || [];
      }

      this.loading = false;
    },
    handleBack() {
      this.$router.push('/order?tab=2');
    },
    handleReorder() {
      Modal.confirm({
        // title: 'Are you sure delete this task?',
        icon: createVNode(ExclamationCircleOutlined),
        content: this.$t('shopping_cart.reorder_confirm'),
        okText: this.$t('shopping_cart.confirm'),
        cancelText: this.$t('shopping_cart.cancel'),
        onOk: async () => {
          if (this.id > 0) {
            this.isCopying = true;
            const res = await copyOrder({
              id: this.id
            });

            if (res.success) {
              this.handleBack();
            }

            this.isCopying = false;
          }
        }
      });
    },
    gotoDiagramDetail(record) {
      if (record.groupId) {
        this.$router.push(`/part-detail/${record.groupId}`);
      } else if (record.vehicleId) {
        this.$router.push(`/spare-part/${record.vehicleId}`);
      }
    }
  },
  computed: {
    ...mapState(['lang', 'userInfo']),
    totalSummary(): string {
      let res = numeral(0);

      this.dataSource.forEach(d => {
        res = res.add(d.totalPrice || 0);
      });

      const moneyIcon = '$';

      const value = res.value();

      return value ? moneyIcon + res.format('0,0.00') : '';
    }
  }
});
</script>

<style lang="scss" scoped>
.container {
  padding: 0 49px;
  background-color: white;

  .title {
    padding: 36px 0 0 0;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .left-section {
      display: flex;
      align-items: center;

      .order-title {
        color: $primary-color;
        font-weight: bold;
        font-size: $secondary-fontsize;
      }

      .back {
        display: flex;
        align-items: center;

        cursor: pointer;

        .icon-label {
          margin-left: 10px;
          font-size: $content-fontsize;
          color: #999999;
        }
      }
    }

    .action-section {
      display: flex;
      align-items: center;
      cursor: pointer;
      color: $primary-color;
      .action-label {
        margin-left: 10px;
      }

      &:hover {
        color: $primary-hover-color;
      }
    }
  }

  .info {
    margin-bottom: 30px;
  }

  .table-container {
    margin-bottom: 40px;

    .table-title {
      margin-bottom: 20px;
      font-weight: bold;
      font-size: $secondary-fontsize;
    }

    .table-summary {
      margin-top: 20px;
      background: #e5eaf4;
      height: 60px;

      display: flex;
      align-items: center;
      justify-content: flex-end;

      .price {
        font-size: $secondary-fontsize;
        color: $primary-color;
        font-weight: bold;
        margin: 0 40px 0 20px;
      }

      .submit-btn {
        padding: 0 50px;
        height: 100%;
      }
    }
  }
}

::v-deep(.ant-descriptions-item-label) {
  color: #999999;
  min-width: 100px;
}
</style>
